import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HelpCircle, Plus, Pencil, Trash2, Loader2 } from "lucide-react";

export default function Faqs() {
  const qc = useQueryClient();
  const { data: faqs, isLoading } = useQuery({
    queryKey: ["admin-faqs"],
    queryFn: async () => (await api.get("/admin/faqs")).data,
  });

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "" });

  const openNew = () => { setEditId(null); setForm({ question: "", answer: "" }); setOpen(true); };
  const openEdit = (f) => { setEditId(f.id); setForm({ question: f.question, answer: f.answer }); setOpen(true); };

  const save = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/admin/faqs/${editId}`, form)).data;
      return (await api.post("/admin/faqs", form)).data;
    },
    onSuccess: () => {
      toast.success(editId ? "FAQ updated" : "FAQ added");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-faqs"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/faqs/${id}`)).data,
    onSuccess: () => {
      toast.success("FAQ deleted");
      qc.invalidateQueries({ queryKey: ["admin-faqs"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const valid = form.question.trim() && form.answer.trim();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Help centre</p>
          <p className="text-sm text-muted-foreground mt-1">Post questions and answers your authors can browse from their dashboard.</p>
        </div>
        <Button data-testid="add-faq-button" onClick={openNew} className="gap-2">
          <Plus className="h-4 w-4" /> Add FAQ
        </Button>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : faqs.length === 0 ? (
        <EmptyState icon={HelpCircle} title="No FAQs yet" desc="Add a question and answer to help your authors." />
      ) : (
        <div className="space-y-4">
          {faqs.map((f) => (
            <Card key={f.id} data-testid={`faq-card-${f.id}`} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-head font-semibold flex items-start gap-2">
                    <HelpCircle className="h-4 w-4 text-primary mt-1 shrink-0" /> {f.question}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{f.answer}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-faq-${f.id}`} onClick={() => openEdit(f)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-faq-${f.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this FAQ?</AlertDialogTitle>
                        <AlertDialogDescription>Authors will no longer see this question.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => del.mutate(f.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head">{editId ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
            <DialogDescription>Write a clear question and a helpful answer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Question</Label>
              <Input data-testid="faq-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="How is my available balance calculated?" />
            </div>
            <div className="space-y-2">
              <Label>Answer</Label>
              <Textarea data-testid="faq-answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Your available balance is your total royalty profit minus approved and pending payouts." className="min-h-[140px]" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-faq-button" onClick={() => save.mutate()} disabled={!valid || save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Save changes" : "Add FAQ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
